import zxcvbn from 'zxcvbn';
import { NumbColorFunc, StringNumFunc } from 'types/password';

const BLACKLISTED_PASSWORDS = [
  'test@123',
  'admin@123',
  'password@123',
  'welcome@123',
  'root@123',
  'user@123',
  'india@123',
  'company@123',
  'pass@123',
  '12345678',
  'password',
  'admin',
  'qwerty'
];

// set color based on password strength
export const strengthColor: NumbColorFunc = (count) => {
  if (count === -1) return { label: 'Commonly used password', color: 'error.dark' };
  if (count === 0) return { label: 'Poor', color: 'error.main' };
  if (count === 1) return { label: 'Weak', color: 'warning.main' };
  if (count === 2) return { label: 'Normal', color: 'warning.dark' };
  if (count === 3) return { label: 'Good', color: 'success.main' };
  if (count === 4) return { label: 'Strong', color: 'success.dark' };
  return { label: 'Poor', color: 'error.main' };
};

// password strength indicator
export const strengthIndicator: StringNumFunc = (number) => {
  if (!number) return 0;
  
  if (BLACKLISTED_PASSWORDS.includes(number.toLowerCase())) {
    return -1;
  }
  
  // Pass blacklist to zxcvbn as custom dictionary so variants are also heavily penalized
  let score: number = zxcvbn(number, BLACKLISTED_PASSWORDS).score;

  // Check for 3 or more identical consecutive characters/numbers (e.g., "aaa", "111")
  const hasRepeatingChars = /(.)\1{2,}/.test(number);
  
  // Check for common consecutive sequential characters or numbers (e.g., "abc", "123", "321")
  const hasSequentialChars = /(012|123|234|345|456|567|678|789|987|876|765|654|543|432|321|210|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|zyx|yxw|xwv|wvu|vut|uts|tsr|srq|rqp|qpo|pon|onm|nml|mlk|lkj|kji|jih|ihg|hgf|gfe|fed|edc|dcb|cba)/i.test(number);

  // Check for repetitive words or character chunks of length 3+ (e.g., "testtest", "passpass")
  const hasRepetitiveWords = /(.{3,})\1+/.test(number);

  // Cap the score to maximum 1 ("Weak") if any of these insecure patterns are detected
  if (hasRepeatingChars || hasSequentialChars || hasRepetitiveWords) {
    score = Math.min(score, 1);
  }

  // Composition requirements
  const hasNumber = /[0-9]/.test(number);
  const hasSpecial = /[!#@$%^&*)(+=._-]/.test(number);
  const hasMixed = /[a-z]/.test(number) && /[A-Z]/.test(number);

  // If a password lacks a number, special character, or mixed casing, cap it at "Normal" (score 2)
  // This prevents long alphabetic strings like "Testfdklsjdfldjsfjdslf" from scoring "Strong"
  if (!hasNumber || !hasSpecial || !hasMixed) {
    score = Math.min(score, 2);
  }

  return score;
};
